/**
 * Generates a valid .ico file in-browser from an array of PNG blobs.
 * The ICO format embeds the raw PNG data directly (supported by all modern browsers).
 *
 * @param {Array<{size: number, blob: Blob}>} entries - sorted smallest first
 * @returns {Promise<Blob>} - blob with mime type 'image/x-icon'
 */
export async function generateFaviconIco(entries) {
  const pngBuffers = await Promise.all(
    entries.map(async ({ blob }) => new Uint8Array(await blob.arrayBuffer()))
  );

  const ICONDIR_SIZE = 6;
  const ICONDIRENTRY_SIZE = 16;

  const headerBytes = ICONDIR_SIZE + ICONDIRENTRY_SIZE * entries.length;
  const totalBytes = headerBytes + pngBuffers.reduce((s, b) => s + b.length, 0);

  const buffer = new ArrayBuffer(totalBytes);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // ICONDIR header
  view.setUint16(0, 0, true); // reserved
  view.setUint16(2, 1, true); // type: 1 = .ico
  view.setUint16(4, entries.length, true); // image count

  let dataOffset = headerBytes;

  pngBuffers.forEach((png, i) => {
    const { size } = entries[i];
    const base = ICONDIR_SIZE + i * ICONDIRENTRY_SIZE;

    view.setUint8(base + 0, size >= 256 ? 0 : size); // width (0 means 256)
    view.setUint8(base + 1, size >= 256 ? 0 : size); // height
    view.setUint8(base + 2, 0); // color count (0 = 256+)
    view.setUint8(base + 3, 0); // reserved
    view.setUint16(base + 4, 1, true); // color planes
    view.setUint16(base + 6, 32, true); // bits per pixel
    view.setUint32(base + 8, png.length, true); // byte size of image data
    view.setUint32(base + 12, dataOffset, true); // offset of image data

    bytes.set(png, dataOffset);
    dataOffset += png.length;
  });

  return new Blob([buffer], { type: 'image/x-icon' });
}
